import PropTypes from "prop-types";
import ComplianceIndicator from "./ComplianceIndicator";
import {
  formatComplaintDate,
  formatComplaintIssuesText,
  formatComplaintNCycles,
  getComplaintComplianceIndicator,
  getComplaintsWithIssues,
} from "../utils/prescriptionComplianceHelpers";

const thClass =
  "border-b border-[#e0e0e0] px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[#3c3c3c]";
const tdClass = "border-b border-[#e8e8e8] px-3 py-3 align-top text-sm text-[#3c3c3c]";

const NonComplianceHistoryPanel = ({
  complaints = [],
  indicatorsByPrescriptionId = {},
  loading = false,
  complaintUserKey = null,
}) => {
  const withIssues = getComplaintsWithIssues(complaints);

  if (!loading && !complaintUserKey) {
    return (
      <div
        className="px-4 py-8 text-sm leading-relaxed text-[#8c8c8c]"
        data-testid="view-paf-noncompliance-unavailable"
      >
        Unable to load non-compliance history for this patient.
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="px-4 py-8 text-sm leading-relaxed text-[#8c8c8c]"
        data-testid="view-paf-noncompliance-loading"
      >
        Loading non-compliance history…
      </div>
    );
  }

  if (withIssues.length === 0) {
    return (
      <div
        className="px-4 py-8 text-sm leading-relaxed text-[#8c8c8c]"
        data-testid="view-paf-noncompliance-empty"
      >
        No compliance issues recorded for this patient.
      </div>
    );
  }

  return (
    <div
      className="overflow-x-auto px-1"
      data-testid="view-paf-noncompliance-panel"
    >
      <table
        className="w-full min-w-[640px] border-collapse text-left"
        data-testid="view-paf-noncompliance-table"
      >
        <thead>
          <tr className="bg-[#fafafa]">
            <th className={thClass}>PAF Number</th>
            <th className={thClass}>PAF Date</th>
            <th className={thClass}>Total Cycles</th>
            <th className={thClass}>Compliance Indicator</th>
            <th className={thClass}>Compliance issue</th>
          </tr>
        </thead>
        <tbody>
          {withIssues.map((complaint) => {
            const prescriptionId = complaint.prescription;
            const rowKey = `${prescriptionId}-${complaint.created_at}`;
            const fromComplaint = getComplaintComplianceIndicator(complaint);
            const fromPrescription =
              indicatorsByPrescriptionId[String(prescriptionId)];
            const indicator =
              fromComplaint === true || fromComplaint === false
                ? fromComplaint
                : fromPrescription;

            return (
              <tr
                key={rowKey}
                className="bg-white"
                data-testid={`view-paf-noncompliance-row-${prescriptionId}`}
              >
                <td className={cnTd()}>{prescriptionId ?? "—"}</td>
                <td className={cnTd()}>
                  {formatComplaintDate(complaint.prescription_created_date)}
                </td>
                <td className={cnTd()}>{formatComplaintNCycles(complaint.n_cycles)}</td>
                <td className={cnTd()}>
                  <ComplianceIndicator value={indicator} showLabel />
                </td>
                <td className={cnTd("text-red-700")}>
                  {formatComplaintIssuesText(complaint)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function cnTd(extra = "") {
  return extra ? `${tdClass} ${extra}` : tdClass;
}

NonComplianceHistoryPanel.propTypes = {
  complaints: PropTypes.array,
  indicatorsByPrescriptionId: PropTypes.object,
  loading: PropTypes.bool,
  complaintUserKey: PropTypes.string,
};

export default NonComplianceHistoryPanel;
